import React, { useEffect, useMemo, useRef, useState } from 'react';

import { fetchVisitJourney } from '../../services/api/visitJourneyApi';
import { useRealtimeCallback } from '../../hooks/useRealtimeEvent';
import { visitPaymentDueRupees } from '../../utils/visitMoney';

/**
 * VisitJourney — single-Query event-log driven timeline.
 *
 * Backend contract (visit_journey_service.build_journey):
 *   { visit_id, headline: { tone, icon, text },
 *     snapshot: { current_doctor_*, action_pending_*, ... },
 *     meta: { totals, timing, current_location, doctors_involved },
 *     lens: 'ADMIN' | 'DOCTOR' | 'RECEPTIONIST' | 'NURSE',
 *     events: [{ event_id, occurred_at, event_kind, actor, target,
 *                title, icon, details, refs, money }] }
 *
 * The frontend renders titles, icons, and the curated `details` rows
 * verbatim. There is no folding, no payload pass-through, no
 * JSON.stringify anywhere on the user-visible path.
 *
 * Props:
 *   visitId          — required.
 *   viewerMemberId   — optional; when it matches
 *                      snapshot.action_pending_with_member_id, the
 *                      "Resolve →" affordance is rendered.
 *   viewerRole       — passed as `?lens=` to scope the response
 *                      (defaults to caller's role if omitted).
 *   compact          — true (default) → 64px strip with last 3 events;
 *                      false → inline full timeline.
 *   onResolveAction  — optional callback when the viewer-as-holder taps
 *                      "Resolve →".
 */

// Legacy sitting rows arrive as bare UTC ISO ("2026-05-17T10:30:00") and
// new rows arrive as IST-offset ("2026-05-17T16:00:00+05:30"). Without a
// timezone marker JS Date() treats the string as local time, which on
// IST clients drifts 5h30m. Force-tag bare strings as UTC so both shapes
// resolve to the same wall-clock moment.
function parseClinicalTs(iso) {
  if (!iso) return null;
  const hasTz = /(Z|[+-]\d{2}:?\d{2})$/.test(iso);
  const d = new Date(hasTz ? iso : `${iso}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatRelative(iso) {
  const d = parseClinicalTs(iso);
  if (!d) return iso || '';
  const diffMs = Date.now() - d.getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return `${days}d ago`;
}

function formatAbsolute(iso) {
  const d = parseClinicalTs(iso);
  if (!d) return iso || '';
  // Local-time HH:MM on dates other than today, otherwise just HH:MM.
  const today = new Date();
  const sameDay =
    d.getFullYear() === today.getFullYear()
    && d.getMonth() === today.getMonth()
    && d.getDate() === today.getDate();
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  if (sameDay) return `${hh}:${mm}`;
  return `${d.toLocaleDateString()} ${hh}:${mm}`;
}

function formatRupees(paise) {
  if (paise == null) return '—';
  const n = Number(paise);
  if (!Number.isFinite(n)) return '—';
  return `₹${Math.round(n / 100).toLocaleString('en-IN')}`;
}

function formatElapsed(seconds) {
  if (!seconds || !Number.isFinite(seconds) || seconds <= 0) return '—';
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins} min`;
  const hrs = Math.floor(mins / 60);
  const rem = mins % 60;
  return rem ? `${hrs}h ${rem}m` : `${hrs}h`;
}

// Viewer-perspective title patch: when the current viewer initiated a
// doctor swap (i.e. they sent the visit away), surface that personally
// instead of the generic backend title. Server titles stay authoritative
// for every other event kind.
function patchEventForViewer(event, viewerMemberId) {
  if (!event || !viewerMemberId) return event;
  if (event.event_kind !== 'DOCTOR_SWAP') return event;
  const actorId = (event.actor || {}).member_id;
  const targetId = (event.target || {}).member_id;
  if (actorId !== viewerMemberId) return event;
  if (targetId === viewerMemberId) return event;
  const targetName = (event.target || {}).name || 'another doctor';
  return { ...event, title: `You forwarded this to Dr. ${targetName}` };
}

const HEADLINE_PALETTE = {
  completed: 'border-emerald-400 bg-emerald-50 text-emerald-900 dark:border-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-100',
  cancelled: 'border-slate-300 bg-slate-50 text-slate-700 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200',
  parked:    'border-amber-400 bg-amber-50 text-amber-900 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-100',
  active:    'border-indigo-400 bg-indigo-50 text-indigo-900 dark:border-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-100',
  queue:     'border-slate-300 bg-slate-50 text-slate-700 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200',
  muted:     'border-slate-300 bg-slate-50 text-slate-700 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200',
};


export default function VisitJourney({
  visitId, viewerMemberId, viewerRole, compact = true, onResolveAction,
}) {
  const requestIdRef = useRef(0);
  const [snapshot, setSnapshot] = useState(null);
  const [headline, setHeadline] = useState(null);
  const [meta, setMeta] = useState(null);
  const [events, setEvents] = useState([]);
  const [lens, setLens] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const reload = useMemo(
    () => async () => {
      if (!visitId) return;
      const myReq = requestIdRef.current + 1;
      requestIdRef.current = myReq;
      setLoading(true);
      try {
        const lensArg = viewerRole ? viewerRole.toLowerCase() : null;
        const response = await fetchVisitJourney(visitId, { lens: lensArg });
        if (myReq !== requestIdRef.current) return;
        setSnapshot(response?.snapshot || null);
        setHeadline(response?.headline || null);
        setMeta(response?.meta || null);
        setLens((response?.lens || viewerRole || '').toUpperCase());
        setEvents(Array.isArray(response?.events) ? response.events : []);
        setError(null);
      } catch (err) {
        if (myReq !== requestIdRef.current) return;
        setError(err?.response?.data?.detail || err?.message || 'Could not load journey');
      } finally {
        if (myReq === requestIdRef.current) setLoading(false);
      }
    },
    [visitId, viewerRole],
  );

  useEffect(() => { reload(); }, [reload]);

  // Live refresh on any visit-touching realtime event.
  useRealtimeCallback(
    [
      'VISIT_DOCTOR_CHANGED',
      'VISIT_ACTION_PENDING_SET',
      'VISIT_ACTION_PENDING_RESOLVED',
      'VISIT_METADATA_ADDED',
      'VISIT_STATUS_CHANGED',
      'SITTING_COMPLETED',
      'SITTING_SCHEDULED',
      'SITTING_PAID',
      'SITTING_CANCELLED',
      'PLAN_ITEM_CONSUMED',
      'VISIT_BUNDLED_PAYMENT_COLLECTED',
    ],
    (evt) => {
      const evtVisitId = evt?.payload?.visitId || evt?.payload?.visit_id;
      if (!evtVisitId || evtVisitId === visitId) reload();
    },
    [visitId, reload],
  );

  const canActAsHolder = (
    viewerMemberId
    && snapshot?.action_pending_with_member_id === viewerMemberId
  );

  // Empty-by-design — no events, no headline → render nothing.
  if (!loading && !error && events.length === 0 && !headline) return null;

  if (compact) {
    return (
      <CompactStrip
        loading={loading}
        error={error}
        headline={headline}
        meta={meta}
        events={events}
        canActAsHolder={canActAsHolder}
        onResolveAction={onResolveAction}
        snapshot={snapshot}
        lens={lens}
        viewerMemberId={viewerMemberId}
      />
    );
  }
  return (
    <FullTimeline
      loading={loading}
      error={error}
      headline={headline}
      meta={meta}
      events={events}
      snapshot={snapshot}
      lens={lens}
      viewerMemberId={viewerMemberId}
    />
  );
}


/* ────────────────────────────────────────────────────────────
 * CompactStrip — headline + last 3 events + [Full journey] modal.
 * ────────────────────────────────────────────────────────────*/

function CompactStrip({
  loading, error, headline, meta, events, snapshot,
  canActAsHolder, onResolveAction, lens, viewerMemberId,
}) {
  const [open, setOpen] = useState(false);
  const patchedEvents = useMemo(
    () => (events || []).map((e) => patchEventForViewer(e, viewerMemberId)),
    [events, viewerMemberId],
  );

  if (loading && events.length === 0 && !headline) {
    return (
      <p className="text-xs italic text-slate-500 dark:text-slate-400 my-1">
        Loading visit journey…
      </p>
    );
  }
  if (error) {
    return (
      <p className="text-xs text-rose-600 dark:text-rose-300 my-1">
        Visit journey unavailable: {error}
      </p>
    );
  }

  const tail = patchedEvents.slice(-3);
  const palette = HEADLINE_PALETTE[headline?.tone] || HEADLINE_PALETTE.muted;

  return (
    <>
      <div className={`rounded-md border px-2.5 py-1.5 text-sm ${palette}`}>
        <div className="flex items-center gap-2 flex-wrap">
          {headline && (
            <span className="font-semibold flex items-center gap-1">
              <span aria-hidden="true">{headline.icon}</span>
              <span>{headline.text}</span>
            </span>
          )}
          {canActAsHolder && onResolveAction && (
            <button
              type="button"
              onClick={onResolveAction}
              className="px-2 py-0.5 text-[11px] font-medium rounded-md bg-orange-600 hover:bg-orange-700 text-white"
            >
              Resolve →
            </button>
          )}
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="ml-auto text-[11px] underline text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-slate-50"
          >
            Full journey
          </button>
        </div>

        {tail.length > 0 && (
          <div className="mt-1.5 flex items-center gap-1 flex-wrap">
            {tail.map((e, i) => (
              <span
                key={e.event_id || i}
                className="inline-flex items-center gap-1 rounded-full border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-0.5 text-[11px] text-slate-700 dark:text-slate-200"
                title={e.title}
              >
                <span aria-hidden="true">{e.icon}</span>
                <span className="max-w-[18rem] truncate">{e.title}</span>
                <span className="text-[10px] opacity-60">{formatRelative(e.occurred_at)}</span>
              </span>
            ))}
          </div>
        )}
      </div>

      {open && (
        <FullDialog
          headline={headline}
          meta={meta}
          events={events}
          snapshot={snapshot}
          lens={lens}
          viewerMemberId={viewerMemberId}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}


/* ────────────────────────────────────────────────────────────
 * FullDialog — modal-wrapped FullTimeline.
 * ────────────────────────────────────────────────────────────*/

function FullDialog({ headline, meta, events, snapshot, lens, viewerMemberId, onClose }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 backdrop-blur-sm"
    >
      <div className="relative w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-2xl">
        <header className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-3">
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
            Patient journey
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-sm px-2 py-1 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
          >
            Close
          </button>
        </header>
        <div className="p-4">
          <FullTimeline
            loading={false} error={null}
            headline={headline} meta={meta} events={events} snapshot={snapshot}
            lens={lens} viewerMemberId={viewerMemberId}
          />
        </div>
      </div>
    </div>
  );
}


/* ────────────────────────────────────────────────────────────
 * FullTimeline — meta summary + every event with its detail rows.
 * ────────────────────────────────────────────────────────────*/

function FullTimeline({ loading, error, headline, meta, events, snapshot, lens, viewerMemberId }) {
  const patchedEvents = useMemo(
    () => (events || []).map((e) => patchEventForViewer(e, viewerMemberId)),
    [events, viewerMemberId],
  );
  if (loading && events.length === 0 && !headline) {
    return <p className="text-sm italic text-slate-500 dark:text-slate-400">Loading…</p>;
  }
  if (error) {
    return <p className="text-sm text-rose-600 dark:text-rose-300">{error}</p>;
  }

  const palette = HEADLINE_PALETTE[headline?.tone] || HEADLINE_PALETTE.muted;
  const totalAmount = meta?.totals?.amount_paise || 0;
  const byDoctor = meta?.totals?.by_doctor || {};
  const doctorsInvolved = meta?.doctors_involved || [];
  const doctorNameById = doctorsInvolved.reduce((acc, d) => {
    if (d?.member_id) acc[d.member_id] = d.name || d.member_id;
    return acc;
  }, {});

  return (
    <div className="flex flex-col gap-3">
      {/* Headline */}
      {headline && (
        <div className={`rounded-md border px-3 py-2 ${palette}`}>
          <div className="text-sm font-semibold flex items-center gap-2">
            <span aria-hidden="true">{headline.icon}</span>
            <span>{headline.text}</span>
          </div>
        </div>
      )}

      {/* Per-role primary card */}
      <PrimaryCard
        lens={lens}
        viewerMemberId={viewerMemberId}
        snapshot={snapshot}
        meta={meta}
        events={events}
      />

      {/* Meta summary card */}
      {meta && (
        <div className="rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-xs">
          <div className="flex flex-wrap gap-x-6 gap-y-1">
            {totalAmount > 0 && (
              <div>
                <span className="text-slate-500 dark:text-slate-400">Total: </span>
                <span className="font-semibold text-slate-900 dark:text-slate-100">
                  {formatRupees(totalAmount)}
                </span>
              </div>
            )}
            {Object.entries(byDoctor).map(([docId, paise]) => (
              <div key={docId}>
                <span className="text-slate-500 dark:text-slate-400">
                  {doctorNameById[docId] ? `Dr. ${doctorNameById[docId]}: ` : 'Dr · '}
                </span>
                <span className="font-medium text-slate-900 dark:text-slate-100">
                  {formatRupees(paise)}
                </span>
              </div>
            ))}
            {meta.timing?.queue_added_at && (
              <div>
                <span className="text-slate-500 dark:text-slate-400">Started: </span>
                <span className="text-slate-700 dark:text-slate-200">
                  {formatAbsolute(meta.timing.queue_added_at)}
                </span>
              </div>
            )}
            {meta.timing?.completed_at && (
              <div>
                <span className="text-slate-500 dark:text-slate-400">Closed: </span>
                <span className="text-slate-700 dark:text-slate-200">
                  {formatAbsolute(meta.timing.completed_at)}
                </span>
              </div>
            )}
          </div>
          {doctorsInvolved.length > 1 && (
            <div className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
              Doctors involved: {doctorsInvolved.map((d) => `Dr. ${d.name || d.member_id}`).join(' → ')}
            </div>
          )}
        </div>
      )}

      {/* Event timeline */}
      {patchedEvents.length === 0 ? (
        <p className="text-sm italic text-slate-500 dark:text-slate-400">
          No journey events recorded yet.
        </p>
      ) : (
        <ol className="flex flex-col gap-1.5">
          {patchedEvents.map((e, i) => (
            <EventRow key={e.event_id || `${e.event_kind}-${i}`} event={e} />
          ))}
        </ol>
      )}
    </div>
  );
}


/* ────────────────────────────────────────────────────────────
 * PrimaryCard — per-role primary card surfaced above the meta
 * summary. Each role gets a card tailored to what they actually need
 * to know first when they open the journey.
 *   ADMIN       → BillingRollupCard
 *   RECEPTIONIST→ WhereNowCard
 *   DOCTOR      → FromPreviousDoctorCard (only when current doctor
 *                  inherited the visit via referral)
 *   NURSE       → no primary card (their lens is already strict)
 * ────────────────────────────────────────────────────────────*/

function PrimaryCard({ lens, viewerMemberId, snapshot, meta, events }) {
  const role = (lens || '').toUpperCase();
  if (role === 'ADMIN') return <BillingRollupCard meta={meta} />;
  if (role === 'RECEPTIONIST') return <WhereNowCard snapshot={snapshot} meta={meta} />;
  if (role === 'DOCTOR') {
    return (
      <FromPreviousDoctorCard
        snapshot={snapshot}
        meta={meta}
        events={events}
        viewerMemberId={viewerMemberId}
      />
    );
  }
  return null;
}

function BillingRollupCard({ meta }) {
  if (!meta) return null;
  const total = meta?.totals?.amount_paise || 0;
  const byDoctor = meta?.totals?.by_doctor || {};
  const involved = meta?.doctors_involved || [];
  const nameById = involved.reduce((acc, d) => {
    if (d?.member_id) acc[d.member_id] = d.name || d.member_id;
    return acc;
  }, {});
  if (total === 0 && Object.keys(byDoctor).length === 0) return null;

  return (
    <div className="rounded-md border border-indigo-300 bg-indigo-50 dark:border-indigo-700 dark:bg-indigo-900/30 px-3 py-2">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-indigo-700 dark:text-indigo-200">
        💰 Billing rollup
      </div>
      <div className="mt-1 flex items-baseline gap-3 flex-wrap">
        <div>
          <span className="text-xs text-indigo-600 dark:text-indigo-300">Total: </span>
          <span className="text-base font-bold text-indigo-900 dark:text-indigo-100">
            {formatRupees(total)}
          </span>
        </div>
        {meta.timing?.queue_added_at && meta.timing?.completed_at && (
          <div className="text-xs text-indigo-700 dark:text-indigo-200">
            Duration: {formatElapsed(
              (new Date(meta.timing.completed_at).getTime()
              - new Date(meta.timing.queue_added_at).getTime()) / 1000,
            )}
          </div>
        )}
      </div>
      {Object.keys(byDoctor).length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {Object.entries(byDoctor).map(([docId, paise]) => (
            <span
              key={docId}
              className="inline-flex items-center gap-1 rounded-full border border-indigo-300 dark:border-indigo-700 bg-white dark:bg-slate-800 px-2 py-0.5 text-[11px] text-indigo-900 dark:text-indigo-100"
            >
              Dr. {nameById[docId] || docId.slice(0, 6) + '…'} · <strong>{formatRupees(paise)}</strong>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function WhereNowCard({ snapshot, meta }) {
  if (!snapshot) return null;
  const status = (snapshot.current_status || '').toUpperCase();
  const isParked = !!snapshot.action_pending_with_member_id;
  const queueNum = snapshot.queue_number;

  let mainLine = null;
  let detail = null;
  let icon = '👤';
  let toneClass = 'border-amber-400 bg-amber-50 text-amber-900 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-100';

  if (status === 'COMPLETED') {
    icon = '✅';
    toneClass = 'border-emerald-400 bg-emerald-50 text-emerald-900 dark:border-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-100';
    mainLine = 'Visit completed';
    detail = (
      meta?.totals?.amount_paise
        ? `Total collected: ${formatRupees(meta.totals.amount_paise)}`
        : null
    );
  } else if (status === 'CANCELLED') {
    icon = '⊘';
    toneClass = 'border-slate-300 bg-slate-50 text-slate-700 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200';
    mainLine = 'Visit cancelled';
  } else if (isParked) {
    icon = '🟡';
    const role = snapshot.action_pending_with_role || 'member';
    const name = snapshot.action_pending_with_name || role;
    const intent = (snapshot.action_pending_intent || '').toUpperCase();
    // Phase 3.5 — `payment_due` is rupees; the local formatRupees() helper
    // expects paise, so convert here to keep comparisons in one unit.
    const dueRupees = visitPaymentDueRupees(snapshot);
    const due = Math.round(dueRupees * 100);
    const collected = snapshot.action_pending_payment_collected_paise || 0;
    const target = snapshot.action_pending_target_doctor_name;

    mainLine = `Parked with ${role} ${name}`;
    if (intent === 'REFERRAL_TO_DOCTOR') {
      const moneyBit = due > 0
        ? (collected >= due
            ? `✓ ${formatRupees(collected)} collected`
            : `${formatRupees(due)} due`)
        : 'no fee';
      const targetBit = target ? ` → Dr. ${target}` : '';
      detail = `${moneyBit}${targetBit}`;
    } else if (due > 0) {
      detail = `${formatRupees(due)} to collect (${formatRupees(collected)} collected)`;
    } else if (intent) {
      detail = intent.replace(/_/g, ' ').toLowerCase();
    }
  } else {
    const doc = snapshot.current_doctor_name || 'doctor';
    mainLine = `With Dr. ${doc}`;
    if (queueNum != null) detail = `Queue Q-${parseInt(queueNum, 10) || queueNum}`;
  }

  return (
    <div className={`rounded-md border px-3 py-2 ${toneClass}`}>
      <div className="text-[11px] font-semibold uppercase tracking-wide opacity-80">
        📍 Where the patient is now
      </div>
      <div className="mt-1 text-sm font-bold flex items-center gap-2">
        <span aria-hidden="true">{icon}</span>
        <span>{mainLine}</span>
      </div>
      {detail && <div className="text-xs mt-0.5 opacity-90">{detail}</div>}
      {meta?.totals?.amount_paise > 0 && status !== 'COMPLETED' && (
        <div className="text-[11px] mt-1 opacity-75">
          Collected so far: {formatRupees(meta.totals.amount_paise)}
        </div>
      )}
    </div>
  );
}

function FromPreviousDoctorCard({ snapshot, meta, events, viewerMemberId }) {
  // Only render when the current viewer is a doctor AND they inherited
  // this visit via a routing event (i.e. the visit's first doctor isn't
  // them). Compute "previous doctor" as the last DOCTOR_SWAP event whose
  // target is the current viewer.
  if (!snapshot || !viewerMemberId) return null;
  if (snapshot.current_doctor_member_id !== viewerMemberId) return null;
  const swaps = (events || []).filter((e) => e.event_kind === 'DOCTOR_SWAP');
  if (swaps.length === 0) return null;

  // The most recent swap that landed on this viewer.
  const inherited = [...swaps].reverse().find(
    (e) => (e.target || {}).member_id === viewerMemberId,
  );
  if (!inherited) return null;
  const fromDoc = inherited.actor || {};
  const reason = (inherited.details || []).find((r) => r.label === 'Reason')?.value;

  // Pull other context the prior doctor recorded BEFORE the swap.
  const swapAt = inherited.occurred_at;
  const priorDoctorId = fromDoc.member_id;
  const isPriorEvent = (e) => {
    if (!e?.occurred_at) return false;
    if (swapAt && e.occurred_at >= swapAt) return false;
    if (priorDoctorId && (e.actor || {}).member_id !== priorDoctorId) return false;
    return true;
  };
  const priorEvents = (events || []).filter(isPriorEvent);
  const rxCount = priorEvents.filter((e) => e.event_kind === 'METADATA_PRESCRIPTION').length;
  const noteCount = priorEvents.filter((e) => e.event_kind === 'METADATA_NOTE').length;
  const vitalCount = priorEvents.filter((e) => e.event_kind === 'METADATA_VITAL').length;
  const symptomCount = priorEvents.filter((e) => e.event_kind === 'METADATA_SYMPTOM').length;
  const procCount = priorEvents.filter(
    (e) => e.event_kind === 'SITTING_COMPLETED' || e.event_kind === 'SITTING_STARTED',
  ).length;

  const items = [];
  if (rxCount) items.push(`📝 ${rxCount} prescription${rxCount > 1 ? 's' : ''}`);
  if (noteCount) items.push(`🗒 ${noteCount} note${noteCount > 1 ? 's' : ''}`);
  if (vitalCount) items.push(`📈 ${vitalCount} vital reading${vitalCount > 1 ? 's' : ''}`);
  if (symptomCount) items.push(`🤒 ${symptomCount} symptom${symptomCount > 1 ? 's' : ''}`);
  if (procCount) items.push(`🪥 ${procCount} procedure${procCount > 1 ? 's' : ''}`);

  return (
    <div className="rounded-md border border-orange-300 bg-orange-50 dark:border-orange-700 dark:bg-orange-900/30 px-3 py-2">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-orange-700 dark:text-orange-200">
        🟠 Sent by previous doctor
      </div>
      <div className="mt-1 text-sm font-bold text-orange-900 dark:text-orange-100">
        From Dr. {fromDoc.name || 'previous doctor'}
      </div>
      {reason && (
        <div className="text-xs mt-0.5 text-orange-900 dark:text-orange-100">
          Reason: {reason}
        </div>
      )}
      {items.length > 0 && (
        <div className="mt-1.5 text-xs text-orange-800 dark:text-orange-200">
          They recorded: {items.join(' · ')}
        </div>
      )}
      {items.length === 0 && (
        <div className="mt-1.5 text-xs italic text-orange-700 dark:text-orange-300">
          No prior clinical notes or procedures recorded on this visit.
        </div>
      )}
    </div>
  );
}


/* ────────────────────────────────────────────────────────────
 * EventRow — single event with optional curated detail rows.
 * Replaces the old <details><pre>{JSON.stringify(payload)}</pre></details>
 * pattern with a structured key/value list.
 * ────────────────────────────────────────────────────────────*/

function EventRow({ event }) {
  const [expanded, setExpanded] = useState(false);
  const details = Array.isArray(event.details) ? event.details : [];
  const hasDetails = details.length > 0;

  return (
    <li className="rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-2.5 py-1.5">
      <div className="flex items-baseline gap-2">
        <span aria-hidden="true" className="text-base leading-none flex-shrink-0">{event.icon || '•'}</span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
            {event.title || event.event_kind}
          </div>
          <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
            {formatAbsolute(event.occurred_at)} · {formatRelative(event.occurred_at)}
          </div>
        </div>
        {hasDetails && (
          <button
            type="button"
            onClick={() => setExpanded((s) => !s)}
            className="text-[11px] underline text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-50 flex-shrink-0"
          >
            {expanded ? 'hide' : 'details'}
          </button>
        )}
      </div>
      {expanded && hasDetails && (
        <dl className="mt-2 grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-[12px] border-t border-slate-200 dark:border-slate-700 pt-2">
          {details.map((row, idx) => (
            <React.Fragment key={idx}>
              <dt className="text-slate-500 dark:text-slate-400">{row.label}</dt>
              <dd className="text-slate-800 dark:text-slate-100 break-words">{String(row.value ?? '—')}</dd>
            </React.Fragment>
          ))}
        </dl>
      )}
    </li>
  );
}
